import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Banknote, Calendar, FileText, Printer, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time', contractor: 'Contractor', freelancer: 'Freelancer' };

export default function MyPayslips() {
  const { token, user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSlip, setExpandedSlip] = useState(null);

  const fetchPayslips = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/payroll/my-payslips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPayslips(await res.json());
    } catch (err) {
      console.error('Fetch payslips error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPayslips(); }, [fetchPayslips]);

  const handleDownloadPDF = async (runId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}/payslip-pdf/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${runId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-[1000px] mx-auto px-6 py-6" data-testid="my-payslips-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Banknote className="w-6 h-6 text-blue-600" />
            My Payslips
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">View your compensation history and download payslips</p>
        </div>

        {payslips.length > 0 ? (
          <Card className="border-slate-200/60 dark:border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 dark:border-slate-700">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Period</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Type</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Hours</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Gross Pay</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Deductions</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Net Pay</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Status</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payslips.map((ps, idx) => {
                    const isExpanded = expandedSlip === ps.run_id;
                    const hasComponents = ps.components && ps.components.length > 0;
                    return (
                      <React.Fragment key={ps.run_id}>
                        <TableRow
                          className={`border-slate-200 dark:border-slate-700 ${hasComponents ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50' : ''}`}
                          onClick={() => hasComponents && setExpandedSlip(isExpanded ? null : ps.run_id)}
                          data-testid={`my-payslip-${idx}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {hasComponents && (
                                <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              )}
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                  {new Date(ps.period_start).toLocaleDateString()} — {new Date(ps.period_end).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-slate-400 capitalize">{ps.pay_type} · {formatCurrency(ps.pay_rate, ps.currency)}/{ps.pay_type === 'hourly' ? 'hr' : 'period'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                              {EMPLOYMENT_LABELS[ps.employment_type] || ps.employment_type}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm text-slate-700 dark:text-slate-300">{ps.tracked_hours}h</span>
                            {ps.overtime_hours > 0 && <span className="text-xs text-amber-500 ml-1">(+{ps.overtime_hours}h OT)</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-700 dark:text-slate-300">
                            {formatCurrency(ps.gross_pay || ps.total_pay, ps.currency)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {ps.total_deductions > 0 ? (
                              <span className="text-red-500">−{formatCurrency(ps.total_deductions, ps.currency)}</span>
                            ) : <span className="text-slate-300">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm font-bold text-emerald-600">{formatCurrency(ps.net_pay || ps.total_pay, ps.currency)}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                              ps.status === 'processed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                            }`}>{ps.status}</span>
                          </TableCell>
                          <TableCell>
                            <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(ps.run_id); }}
                              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-600 transition-colors"
                              title="Download Payslip PDF" data-testid={`download-my-payslip-${idx}`}>
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                        {isExpanded && hasComponents && (
                          <TableRow className="bg-slate-50/50 dark:bg-slate-800/30">
                            <TableCell colSpan={8} className="py-4 px-6">
                              <div className="max-w-md" data-testid={`my-breakdown-${idx}`}>
                                <div className="grid grid-cols-2 gap-6">
                                  <div>
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">Earnings</p>
                                    {ps.components.filter(c => c.category === 'earning').map((c, ci) => (
                                      <div key={ci} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-500">{c.name}</span>
                                        <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(c.amount, ps.currency)}</span>
                                      </div>
                                    ))}
                                    <div className="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 flex justify-between text-xs font-bold">
                                      <span>Gross Pay</span>
                                      <span className="font-mono">{formatCurrency(ps.gross_pay || ps.total_pay, ps.currency)}</span>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-2">Deductions</p>
                                    {ps.components.filter(c => c.category === 'deduction').map((c, ci) => (
                                      <div key={ci} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-500">{c.name}</span>
                                        <span className="font-mono font-semibold text-red-500">−{formatCurrency(c.amount, ps.currency)}</span>
                                      </div>
                                    ))}
                                    {ps.components.filter(c => c.category === 'deduction').length === 0 && (
                                      <p className="text-xs text-slate-400 italic">No deductions</p>
                                    )}
                                    {ps.total_deductions > 0 && (
                                      <div className="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 flex justify-between text-xs font-bold">
                                        <span>Total Deductions</span>
                                        <span className="font-mono text-red-600">−{formatCurrency(ps.total_deductions, ps.currency)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="border-t border-slate-300 dark:border-slate-500 mt-3 pt-2 flex justify-between text-sm font-bold">
                                  <span className="text-slate-900 dark:text-white">Net Pay (Take Home)</span>
                                  <span className="text-emerald-600 font-mono">{formatCurrency(ps.net_pay || ps.total_pay, ps.currency)}</span>
                                </div>
                                {ps.salary_template_name && (
                                  <p className="text-[10px] text-slate-400 mt-2">Salary Structure: {ps.salary_template_name}</p>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
            <p className="text-lg font-semibold text-slate-500 dark:text-slate-400">No payslips yet</p>
            <p className="text-sm text-slate-400 mt-1">Your payslips will appear here once payroll is processed</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
